import { JobFinancialsData } from '../Items/JobCosting/jobCostingModel'
import { centsToDollarsDisplay, percentageDisplay } from '../displayFormatters'
import { CsvColumn } from '../Datacenter/reportCsvExport'
import { EmployeeUtilization, GraphingData, SoldJobRevenue } from './dashboardApi'
import { DashboardFilters, hasDashboardFilters } from './dashboardModel'
import { hoursDisplay, unbillablePercentDisplay } from './employeeUtilizationModel'

export const dashboardReportName = (name: string, filters?: DashboardFilters): string =>
  filters && hasDashboardFilters(filters) ? `${name} filtered` : name

// Charts export what they plot: one row per slice.
export type ChartExportRow = { label: string, value: number }

export const chartExportRows = (data: GraphingData | undefined): ChartExportRow[] =>
  data ? data.labels.map((label, i) => ({ label, value: data.datasets[0]?.[i] ?? 0 })) : []

export const chartExportColumns = (valueTitle: string): CsvColumn<ChartExportRow>[] => [
  { title: 'Category', dataIndex: 'label' },
  { title: valueTitle, dataIndex: 'value' },
]

export type SoldJobExportRow = SoldJobRevenue['jobs'][number]

export const soldJobExportColumns: CsvColumn<SoldJobExportRow>[] = [
  { title: 'Job Number', dataIndex: 'jobNumber' },
  { title: 'Name', dataIndex: 'name' },
  { title: 'Customer', dataIndex: 'customerName' },
  { title: 'Revenue', dataIndex: 'revenue', render: (_, record) => centsToDollarsDisplay(record.revenue), exportCents: record => record.revenue },
]

export type FinancialsExportRow = {
  section: string
  metric: string
  value: string | number | null
  cents: number | null
}

const money = (section: string, metric: string, cents: number): FinancialsExportRow =>
  ({ section, metric, value: centsToDollarsDisplay(cents), cents })

// A plain number, so hours can be summed in a spreadsheet; the card rounds them for display.
const hours = (value: number | undefined): number | null => value === undefined ? null : Math.round(value * 100) / 100

const other = (section: string, metric: string, value: string | number | null): FinancialsExportRow =>
  ({ section, metric, value, cents: null })

// The job costing summary as the card shows it, one figure per row. Keep in step with getFormattedData in
// Financials/Financials.tsx, which lays out the same figures for the card.
export const financialsExportRows = (data: JobFinancialsData | undefined): FinancialsExportRow[] => {
  if (!data) {
    return []
  }
  const costs = (section: string, values: { labor: number, materials: number, equipment: number, subcontracting: number }) => [
    money(section, 'Labor', values.labor),
    money(section, 'Materials', values.materials),
    money(section, 'Equipment', values.equipment),
    money(section, 'Subcontracting', values.subcontracting),
  ]

  return [
    money('Revenue', 'Estimated', data.price.estimated),
    money('Revenue', 'Actual', data.price.actual),
    money('Revenue', 'Final', data.price.final),
    other('Gross Profit', 'Estimated %', percentageDisplay(data.grossProfit.estimated.percentage)),
    money('Gross Profit', 'Estimated', data.grossProfit.estimated.amount),
    other('Gross Profit', 'Final %', percentageDisplay(data.grossProfit.final.percentage)),
    money('Gross Profit', 'Final', data.grossProfit.final.amount),
    other('Net Profit', 'Estimated %', percentageDisplay(data.netProfit.estimated.percentage)),
    money('Net Profit', 'Estimated', data.netProfit.estimated.amount),
    other('Net Profit', 'Final %', percentageDisplay(data.netProfit.final.percentage)),
    money('Net Profit', 'Final', data.netProfit.final.amount),
    ...costs('Estimated Cost', data.estimated),
    money('Estimated Cost', 'Overhead', data.estimated.overhead),
    money('Estimated Cost', 'Breakeven', data.estimated.breakeven),
    ...costs('Actual Cost', data.actual),
    money('Actual Cost', 'Overhead', data.actual.overhead),
    money('Actual Cost', 'Breakeven', data.actual.breakeven),
    ...costs('Unbillable Work', data.unbillable),
    other('Labor Hours', 'Actual', hours(data.actual.laborHours)),
    other('Labor Hours', 'Estimated', hours(data.estimated.laborHours)),
    other('Labor Hours', 'Unbillable', hours(data.unbillable.laborHours)),
  ]
}

export const financialsExportColumns: CsvColumn<FinancialsExportRow>[] = [
  { title: 'Section', dataIndex: 'section' },
  { title: 'Metric', dataIndex: 'metric' },
  { title: 'Value', dataIndex: 'value', exportCents: record => record.cents },
]

// The table shows rounded hours and a dash for no hours; the file gets the raw numbers.
export const employeeUtilizationColumns: CsvColumn<EmployeeUtilization>[] = [
  { title: 'Employee', dataIndex: 'name' },
  { title: 'Email', dataIndex: 'email' },
  { title: 'Total Hours', dataIndex: 'totalHours', align: 'right', render: (_, record) => hoursDisplay(record.totalHours), exportValue: record => record.totalHours },
  { title: 'Billable Hours', dataIndex: 'billableHours', align: 'right', render: (_, record) => hoursDisplay(record.billableHours), exportValue: record => record.billableHours },
  { title: 'Unbillable Hours', dataIndex: 'unbillableHours', align: 'right', render: (_, record) => hoursDisplay(record.unbillableHours), exportValue: record => record.unbillableHours },
  { title: 'Unbillable %', dataIndex: 'unbillablePercent', align: 'right', render: (_, record) => unbillablePercentDisplay(record.unbillablePercent), exportValue: record => record.unbillablePercent },
]
